import React, { ReactElement, useCallback, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import download from 'downloadjs';
import isEqual from 'lodash.isequal';
import { compose } from 'redux';
import { parse, stringify } from 'b2b-apps/shop/common/utils/queryParams';
import { useFetchNewsroomAssetsAction, useNewsroomAssetsData } from 'b2b-apps/shop/content/hooks';
import { useRouterPush } from 'b2b-apps/shop/routes/hooks';
import { EnhancedComponentProps, EnhancedComponentType, InjectedProps, WrappedComponentType } from './';

const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement => {
    const { location } = props;
    const push = useRouterPush();
    const fetchAssets = useFetchNewsroomAssetsAction();
    const { data } = useNewsroomAssetsData();
    const currentPage = +parse(location.search).page;

    useEffect(
      () => {
        fetchAssets(currentPage);
      },
      [
        fetchAssets,
        currentPage,
      ],
    );

    const handlePaginationPageChange = useCallback(
      (nextPage: number): void => {
        const newPageParam = {
          page: nextPage,
        };

        push({
          search: stringify(newPageParam),
        });
      },
      [push],
    );

    const handleDownloadButtonClick = useCallback(
      (url: string): void => {
        download(url);
      },
      [],
    );

    const injectedProps: InjectedProps = {
      newsData: data,
      onPaginationPageChange: handlePaginationPageChange,
      onDownloadButtonClick: handleDownloadButtonClick,
      page: currentPage,
    };

    return <Component {...props} {...injectedProps} />;
  };

  return compose<EnhancedComponentType>(
    withRouter,
  )(
    React.memo(WithContainer, (prevProps, nextProps) => isEqual(prevProps, nextProps)),
  );
};

export { withContainer };
